/**
 * Analytics tracker — fire-and-forget pageviews + custom events.
 *
 * Privacy-first: the visitor id is a random value stored in
 * localStorage and rotated every 30 days. We never send IPs (the
 * worker masks those) or PII.
 *
 * Pageviews are tracked automatically via `usePageviewTracker()`,
 * which reads the current location and fires when the path changes.
 *
 * Custom events fire on demand — see `track()` and the helpers.
 */

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

const RAW_API_URL = (
  import.meta.env.VITE_API_URL ?? 'https://api.abdallah-issa.com/api'
).replace(/\/$/, '')

const VISITOR_KEY = 'analytics_visitor_id'
const VISITOR_TTL_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

function generateVisitorId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

function getVisitorId(): string {
  if (typeof window === 'undefined') return ''
  try {
    const raw = localStorage.getItem(VISITOR_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as { id?: string; ts?: number }
      if (parsed?.id && typeof parsed.ts === 'number') {
        if (Date.now() - parsed.ts < VISITOR_TTL_MS) return parsed.id
      }
    }
  } catch {
    /* fall through and reset */
  }
  const id = generateVisitorId()
  try {
    localStorage.setItem(VISITOR_KEY, JSON.stringify({ id, ts: Date.now() }))
  } catch {
    /* private mode or quota — ignored */
  }
  return id
}

function readUtm(search: string) {
  if (!search) return {}
  const params = new URLSearchParams(search)
  return {
    utmSource: params.get('utm_source') ?? undefined,
    utmMedium: params.get('utm_medium') ?? undefined,
    utmCampaign: params.get('utm_campaign') ?? undefined,
  }
}

function getLocale(): string {
  if (typeof document === 'undefined') return 'en'
  const lang = document.documentElement.lang || 'en'
  return lang.toLowerCase().startsWith('ar') ? 'ar' : 'en'
}

/**
 * Send a payload as JSON. Uses navigator.sendBeacon when available so
 * the request still goes out during page unload; falls back to fetch
 * with `keepalive: true` for the same effect on browsers that don't
 * carry sendBeacon (Safari < 13).
 */
function postJson(endpoint: string, payload: unknown): void {
  if (!RAW_API_URL) return
  const url = `${RAW_API_URL}${endpoint}`
  const body = JSON.stringify(payload)
  try {
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      const blob = new Blob([body], { type: 'application/json' })
      navigator.sendBeacon(url, blob)
      return
    }
  } catch {
    /* sendBeacon throws on opaque CORS in some browsers; fall through */
  }
  void fetch(url, {
    method: 'POST',
    body,
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
  }).catch(() => {
    /* analytics failures must never disturb the user */
  })
}

interface PageviewPayload {
  path: string
  title?: string
  referrer?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  locale?: string
}

/** Send a pageview. Safe to call multiple times — server is the source of truth. */
export function trackPageview(input: Partial<PageviewPayload> = {}) {
  const visitorId = getVisitorId()
  if (!visitorId || typeof window === 'undefined') return

  const path =
    input.path ?? `${window.location.pathname}${window.location.search}`
  const title = input.title ?? document.title
  const referrer = input.referrer ?? (document.referrer || undefined)
  const utm = readUtm(window.location.search)

  postJson('/analytics/pageview', {
    visitorId,
    path,
    title,
    referrer,
    utmSource: input.utmSource ?? utm.utmSource,
    utmMedium: input.utmMedium ?? utm.utmMedium,
    utmCampaign: input.utmCampaign ?? utm.utmCampaign,
    locale: input.locale ?? getLocale(),
  })
}

interface EventPayload {
  /** Required: short snake_case identifier (e.g. `prompt_copy`). */
  name: string
  /** Optional: id/slug of the resource the event references. */
  target?: string
  /** Optional: extra dimension (platform name for shares, etc.). */
  value?: string
}

export function trackEvent(input: EventPayload) {
  const visitorId = getVisitorId()
  if (!visitorId || typeof window === 'undefined') return
  postJson('/analytics/event', {
    visitorId,
    eventName: input.name,
    eventTarget: input.target,
    eventValue: input.value,
    path: window.location.pathname,
    locale: getLocale(),
  })
}

/**
 * Hook: fires a pageview every time the route changes. Mount once
 * inside the router so the entire app is covered.
 */
export function usePageviewTracker() {
  const location = useLocation()
  // Skip the very first run if we already pushed before the router
  // mounted (e.g. via the boot script). React Strict Mode mounts
  // twice in dev, so we also guard against duplicate same-location
  // sends with a ref.
  const lastSent = useRef<string | null>(null)

  useEffect(() => {
    const composite = `${location.pathname}${location.search}`
    if (lastSent.current === composite) return
    lastSent.current = composite
    trackPageview({ path: composite })
  }, [location.pathname, location.search])
}
